import { base64ByteLength } from './direct-call-attachments';
import { HumanConversationHttp } from './direct-call-http';

/**
 * US 37331 (Feature 37328 S3): a seat in the call asks to see what this human is showing. The bridge
 * (US 37330) sends SignalR `frameRequest` to every human in the call; the app asks its user, and on a
 * yes posts ONE JPEG to the request. Nothing is sent without that yes.
 */
export interface FrameRequest {
  conversationId: string;
  requestId: string;
  seat: string;
  expiresAt: number;
}

/** A new photo, one from the library (not live: the seat must not read it as what is in view now), or a screen. */
export type FrameSource = 'camera' | 'library' | 'screen';

/** The bridge's caps (LiveWindowPolicy): 400 KB and 1280 px on either edge. */
export const FRAME_MAX_BYTES = 400 * 1024;
export const FRAME_MAX_EDGE = 1280;

/** What became of a posted frame: the seat got it, the ask had closed, or the bridge refused it. */
export type FramePostOutcome = 'sent' | 'expired' | 'refused';

const REQUEST_ID = /^[0-9a-f]{32}$/;
const CONVERSATION_ID = /^[a-zA-Z0-9_-]+$/;

/** A well-formed, still-open ask, or null. The bridge is the authority; this only drops junk early. */
export function parseFrameRequest(
  value: unknown,
  now: number
): FrameRequest | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (
    typeof v['conversationId'] !== 'string' ||
    !CONVERSATION_ID.test(v['conversationId'])
  )
    return null;
  if (typeof v['requestId'] !== 'string' || !REQUEST_ID.test(v['requestId']))
    return null;
  if (typeof v['seat'] !== 'string' || !v['seat'].trim()) return null;
  const expiresAt =
    typeof v['expiresAt'] === 'string' ? Date.parse(v['expiresAt']) : NaN;
  if (!Number.isFinite(expiresAt) || expiresAt <= now) return null;
  return {
    conversationId: v['conversationId'],
    requestId: v['requestId'],
    seat: v['seat'].trim(),
    expiresAt,
  };
}

/** The human call HTTP client plus the frame answer route (kept apart: the base file is at its size cap). */
export class HumanCallHttp extends HumanConversationHttp {
  async postFrame(
    id: string,
    requestId: string,
    frame: { source: FrameSource; dataBase64: string },
    signal: AbortSignal
  ): Promise<FramePostOutcome> {
    if (!REQUEST_ID.test(requestId))
      throw new Error('Invalid frame request id');
    // Refused here rather than uploaded to be refused.
    if (base64ByteLength(frame.dataBase64) > FRAME_MAX_BYTES) return 'refused';
    const response = await this.send(
      `/conversations/${this.conversationId(id)}/frames/requests/${requestId}`,
      'POST',
      signal,
      { source: frame.source, dataBase64: frame.dataBase64 }
    );
    if (response.ok) return 'sent';
    return response.status === 410 || response.status === 404
      ? 'expired'
      : 'refused';
  }
}

/**
 * Holds the app's frame-request handlers across call connections: each new call hub is attached with
 * {@link FrameRequestFeed.attach}, and a handler registered once hears asks on every connection.
 */
export class FrameRequestFeed {
  private readonly handlers = new Set<(request: FrameRequest) => void>();

  constructor(private readonly now: () => number = Date.now) {}

  on(handler: (request: FrameRequest) => void): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  attach(hub: {
    on(type: string, handler: (value: unknown) => void): () => void;
  }): () => void {
    return hub.on('frameRequest', (value) => {
      const request = parseFrameRequest(value, this.now());
      if (request) for (const handler of [...this.handlers]) handler(request);
    });
  }
}
